/**
 * Is this TDO one of a request's input files?
 *
 * THE ONE DEFINITION. It was written twice — once in `mapMediaFiles`
 * (reducers), which decides what the folder column renders, and once in the
 * roster read (services), which decides what Select All, the duplicate-name
 * check and the counts cover. Two copies of "a file in this request" is two
 * answers waiting to disagree, and the VE-30449 review gates flagged the
 * duplication twice (S1, then S-2) before this existed.
 *
 * Three guards, and the order does not matter because they are independent:
 *
 * - SOFT-DELETED. A move to Trash tags the TDO before it relocates it, so a
 *   file whose move did not finish is already excluded here — which is why
 *   this reads the tag rather than the file's folder.
 * - EXPORT. `details.isExport` marks the TDO that carries a Download All
 *   bundle. It lives in the request but is not a file in it.
 * - PROJECT. A project TDO holds the request's COMBINED redacted output. It
 *   is not a source document and must never present as one.
 *
 * `details` is free-form JSON that any editor of a TDO can write, so nothing
 * in it is trusted to have its usual shape: `tags` is checked to be a list
 * rather than assumed to be one. One malformed TDO must not throw and take
 * the whole request's read down with it.
 */
import { isProjectTdo } from '@redact-modules/projects/models';

/** The shape both callers can satisfy — a TDO with free-form `details`. */
export interface RequestMembershipCandidate {
  readonly details?: unknown;
}

const isSoftDeleted = (details: unknown): boolean => {
  const tags: unknown = (details as { tags?: unknown } | null | undefined)
    ?.tags;
  return (
    Array.isArray(tags) &&
    tags.some(
      (tag: unknown) =>
        typeof tag === 'object' &&
        tag !== null &&
        !!(tag as { movedToTrashDateTime?: unknown }).movedToTrashDateTime
    )
  );
};

export const isRequestInputFile = (
  tdo: RequestMembershipCandidate | null | undefined
): boolean => {
  if (!tdo) {
    return false;
  }
  const details = tdo.details;
  return (
    !isSoftDeleted(details) &&
    (details as { isExport?: unknown } | null | undefined)?.isExport !== true &&
    !isProjectTdo(details)
  );
};
